import { appstr } from "../utils/appstr";
import { ConvertNull } from "../utils/ConvertNull";
import { ConvertNullInt } from "../utils/ConvertNullInt.js";
import {
  enumBranchStyle,
  enumBSFlagOptions,
  enumContainerType,
  enumDashStyle,
  enumFieldDisplayMode,
  enumFillMode,
  enumFlagType,
  enumHierarchyTypes,
  enumMatchSubs,
  kBoxOverlayRule,
  kBoxStyleRule,
  kBranchStyleOverlayRule,
  kDynHighlightRule,
  kFieldOverlayRule,
  kGroupingRule,
  kHighlightingRule,
  kInheritedRule,
  kMasterPageRule,
  kMasterSCID,
  kPositionBoxOverlayRule,
  kPositionBoxStyleRule,
  kPositionFieldOverlayRule
} from "../constants";
import { HandleNaN } from "../utils/HandleNaN";
import { normalizeLegacyBoxOverlayData } from "../utils/normalizeLegacyBoxOverlayData";
import { QRule } from "./QRule";
import { Sappstr } from "../utils/Sappstr";
import { StringToBool } from "../utils/StringToBool";
import { GetBSOption } from "../utils/GetBSOption.js";
import { GetFieldLabel } from "../utils/GetFieldLabel";

/**
 * @typedef {import('./QBoxStylesManager').QBoxStylesManager} QBoxStylesManager
 * @typedef {import('./QChartTemplatesMgr').QChartTemplatesMgr} QChartTemplatesMgr
 * @typedef {import('./QXDoc').QXDoc} QXDoc
 */

export class QChartTemplateRule extends QRule {
  constructor() {
    // make one of these
    super();
    this.fRuleCriteriaType = "Basic Rule";
    /**
     * @type {(null | any)}
     */
    this.fActionSet = null;
    this.fHighlightName = null;
    this.fHighlightActive = null;
  }

  hierarchyMap = {
    [kBoxOverlayRule]: enumHierarchyTypes.employees,
    [kPositionBoxOverlayRule]: enumHierarchyTypes.positions,
    [kFieldOverlayRule]: enumHierarchyTypes.employees,
    [kPositionFieldOverlayRule]: enumHierarchyTypes.positions
  };

  InitChartTemplateRule() {
    this.InitRule(enumMatchSubs.matchonly, 0);
    this.fActionSet = {};
  }

  GetIsComplex() {
    return this.fRuleCriteriaType == "Complex Rule";
  }

  SetRuleCriteria(theCriteria) {
    if (["Always Apply", "Basic Rule", "Complex Rule"].includes(theCriteria))
      this.fRuleCriteriaType = theCriteria;
  }

  SetNoRule() {
    this.fRuleCriteriaType = "Always Apply";
  }

  IsNoRule() {
    return this.fRuleCriteriaType == "Always Apply";
  }

  GetRuleCriteriaType() {
    return this.fRuleCriteriaType;
  }

  AddRuleAction(type) {
    this.fActionSet[type] = {};
  }

  DeleteRuleAction(type) {
    delete this.fActionSet[type];
  }

  SetRuleActionEnabled(type, enabled) {
    this.fActionSet[type].fActive = true;
  }

  GetMasterName() {
    return this.fActionSet[kMasterPageRule]
      ? this.fActionSet[kMasterPageRule].fMasterTemplate
      : null;
  }

  SetMasterName(masterTemplate) {
    if (!this.fActionSet[kMasterPageRule]) this.fActionSet[kMasterPageRule] = {};
    this.fActionSet[kMasterPageRule].fMasterTemplate = masterTemplate;
  }

  LoadActionFromJSON(actionType, theData) {
    var newAction = {};
    switch (actionType) {
      case kBoxOverlayRule:
      case kPositionBoxOverlayRule:
        this.fActionSet[actionType] = {};
        if (theData["rpropsenabled"] != null) {
          var valueList = theData["rpropsenabled"].split(",");
          newAction.fShapeEnabled = StringToBool(valueList[0]);
          newAction.fFillEnabled = StringToBool(valueList[1]);
          newAction.fBorderEnabled = StringToBool(valueList[2]);
          newAction.fBorderColorEnabled = StringToBool(valueList[3]);

          if (theData["rlinepropsenabled"]) {
            var valueList2 = theData["rlinepropsenabled"].split(",");
            newAction.fLineColorEnabled = StringToBool(valueList2[0]);
            newAction.fLineDashEnabled = StringToBool(valueList2[1]);
          }

          newAction.fTriFlagEnabled = false;
          var triFlagEnabled = theData["rtrienabled"];
          if (triFlagEnabled != null) newAction.fTriFlagEnabled = StringToBool(triFlagEnabled);

          var valueList3 = theData["rboxprops"].split(",");
          newAction.fBoxShapeType = HandleNaN(parseInt(valueList3[0]));
          newAction.fBoxFillColor = valueList3[1];

          if (theData["rboxpropsadv"]) {
            var valueList3a = theData["rboxpropsadv"].split(",");
            newAction.fBoxFillMode = HandleNaN(parseInt(valueList3a[0]));
            newAction.fBoxFillColorGradient = valueList3a[1];
          }

          var valueList4 = theData["rborderprops"].split(",");
          var bt = ConvertNull(valueList4[0]);
          if (bt != null) bt = HandleNaN(parseInt(bt));
          newAction.fBorderThickness = bt;
          var bs = ConvertNull(valueList4[1]);
          if (bs != null) bs = HandleNaN(parseInt(bs));
          newAction.fBorderStyle = bs;
          newAction.fBoxBorderColor = ConvertNull(valueList4[2]);

          var dashStyle = theData["rborderdash"];
          dashStyle = ConvertNull(dashStyle);
          newAction.fBorderDashStyle = null;
          if (dashStyle != null) newAction.fBorderDashStyle = parseInt(dashStyle);

          if (theData["rlineprops"]) {
            var valueList5 = theData["rlineprops"].split(",");
            newAction.fLineColor = ConvertNull(valueList5[0]);
          } else newAction.fLineColor = "#000000";

          if (theData["rlinetype"]) {
            var valueList7 = theData["rlinetype"].split(",");
            if (valueList7[0]) newAction.fLineType = ConvertNullInt(valueList7[0]);
            if (valueList7[1]) newAction.fLineWeight = ConvertNullInt(valueList7[1]);
          } else if (this.fLineDashEnabled) {
            newAction.fLineType = enumDashStyle.stddash;
            newAction.fLineWeight = 1;
          }

          if (theData["rtprops"]) {
            var valueList6 = theData["rtprops"].split(",");
            newAction.fTriColor1 = ConvertNull(valueList6[0]);
            newAction.fTriColor2 = ConvertNull(valueList6[1]);
            newAction.fTriColor3 = ConvertNull(valueList6[2]);
            newAction.fTriLabel = ConvertNull(valueList6[3]);
          }

          var triPosition = theData["rtriposition"];
          if (triPosition != null) newAction.fTriPosition = triPosition;
          this.fActionSet[actionType] = newAction;
        } else {
          this.fActionSet[actionType] = normalizeLegacyBoxOverlayData(theData);
        }
        break;
      case kFieldOverlayRule:
      case kPositionFieldOverlayRule:
        if (theData["rfpropsenabled"] != null) {
          var valueList = theData["rfpropsenabled"].split(",");
          newAction.fTextColorEnabled = StringToBool(valueList[0]);
          newAction.fCellColorEnabled = StringToBool(valueList[1]);
          newAction.fTextStyleEnabled = StringToBool(valueList[2]);
          newAction.fFieldName = ConvertNull(valueList[3]);

          var valueList = theData["rfieldprops"].split(",");
          newAction.fTextColor = ConvertNull(valueList[0]);
          newAction.fCellColor = ConvertNull(valueList[1]);
          newAction.fCellBold = StringToBool(valueList[2]);
          newAction.fCellItalic = StringToBool(valueList[3]);
          newAction.fCellUnderline = StringToBool(valueList[4]);

          var hideCell = theData["rfieldhide"];
          newAction.fCellShown = !(hideCell === "true");
          this.fActionSet[actionType] = newAction;
        } else {
          this.fActionSet[actionType] = JSON.parse(JSON.stringify(theData));
        }
        break;
      case kBranchStyleOverlayRule:
      case kInheritedRule:
      case kHighlightingRule:
      case kMasterPageRule:
      case kBoxStyleRule:
      case kPositionBoxStyleRule:
        this.fActionSet[actionType] = theData;
        break;
      case "grouping":
        if (theData["groupingFieldEnabled"] != null) {
          this.fActionSet[kGroupingRule] = {};
          var groupenabled = theData["groupingFieldEnabled"];
          groupenabled = StringToBool(groupenabled);
          this.SetGroupingFieldEnabled(groupenabled);
          if (groupenabled) {
            var groupField = theData["groupingField"];
            this.SetGroupingField(groupField);
          }

          var valueList = theData["groupProps"].split(",");
          if (valueList && valueList.length > 3) {
            this.SetGroupingBoxStyle(valueList[0]);
            this.SetChildrenBoxStyleEnabled(StringToBool(valueList[1]));
            if (valueList[2] == "true") this.SetGroupingThreshold(1);
            else if (valueList[2] == "false" || valueList[2] == "undefined" || valueList[2] == null)
              this.SetGroupingThreshold(2);
            else this.SetGroupingThreshold(parseInt(valueList[2]));
            this.SetGroupFromParking(StringToBool(valueList[3]));
          }
          if (this.GetChildrenBoxStyleEnabled()) {
            var childBoxID = theData["childStyle"];
            this.SetChildrenBoxStyle(childBoxID);
          }
          //TODO: These should probably be loaded regardless of the rule type- these are global settings that can be used elsewhere.

          if (theData["applyToProps"]) {
            valueList = theData["applyToProps"].split(",");
            this.SetApplyToManagers(StringToBool(valueList[0]));
            this.SetClearGroups(StringToBool(valueList[1]));
          }
        } else {
          this.fActionSet[kGroupingRule] = JSON.parse(JSON.stringify(theData));
        }
        break;
    }
  }

  OutputRuleToJSON() {
    //TODO: Nonstandard fxn name. Probably want to standardize...
    var ruleJSON = {};
    ruleJSON["type"] = this.fRuleCriteriaType;
    ruleJSON["logicalop"] = this.fLogicalOperator;

    if (this.fRuleCriteriaType == "Basic Rule") {
      ruleJSON["pairs"] = [];
      for (var aPair in this.fRulePairList) {
        var thePair = this.fRulePairList[aPair];
        ruleJSON["pairs"].push(thePair.OutputToJSON());
      }
    } else if (this.fRuleCriteriaType == "Complex Rule") {
      ruleJSON["complexdef"] = this.fComplexDef;
    }

    ruleJSON["actions"] = {};
    for (var anActionKey in this.fActionSet) {
      var theAction = this.fActionSet[anActionKey];
      ruleJSON["actions"][anActionKey] = JSON.parse(JSON.stringify(theAction));
    }

    return ruleJSON;
  }

  LoadFromJSON(theData) {
    this.fRuleCriteriaType = theData["type"];
    this.fLogicalOperator = theData["logicalop"];
    if (this.fLogicalOperator == null) this.fLogicalOperator = "";
    this.fRulePairList = [];
    if (this.fRuleCriteriaType === "Basic Rule") {
      for (var thePairIdx in theData["pairs"]) {
        //Holy frankenrule
        var thePair = theData["pairs"][thePairIdx];
        this.AddRulePair(
          thePair["fieldName"],
          thePair["fieldID"],
          thePair["fieldClassName"] ?? "Box",
          thePair["operator"],
          thePair["value"]
        );
      }
    } else if (this.fRuleCriteriaType === "Complex Rule") {
      if (!theData["complexdef"]) theData["complexdef"] = "";
      this.fComplexDef = theData["complexdef"];
    }

    if ("actions" in theData) {
      this.fActionSet = {};
      for (var anActionKey in theData["actions"]) {
        var theAction = theData["actions"][anActionKey];
        this.LoadActionFromJSON(anActionKey, theAction);
      }
    }
  }

  GetBoxStyleID(actionType = kBoxStyleRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBoxStyleID : null;
  }

  SetBoxStyleID(boxStyleID, actionType = kBoxStyleRule) {
    if (!this.fActionSet[actionType]) this.fActionSet[actionType] = {};
    this.fActionSet[actionType].fBoxStyleID = boxStyleID;
  }

  GetMatchingSCNodes(nodeList, excludeSubChartID, subChartMgr, theDoc, ruleAction) {
    var matchList = [];
    var n = nodeList.length;

    for (var i = 0; i < n; i++) {
      var node = nodeList[i];
      var isLocked = node.IsLayoutLocked();

      var dbObj = node.GetDataObj();
      var scID = node.GetSubChartID();
      if (
        !isLocked &&
        scID !== kMasterSCID &&
        (scID != excludeSubChartID || (dbObj && dbObj.IsFiltered()))
      ) {
        if (dbObj) {
          var sc = subChartMgr.FindSubChart(scID);
          if (sc && sc.IsInMainSubChart(dbObj)) {
            var layout = node.GetLayout();
            var topObj = subChartMgr.FindTopObjFromLayout(layout);
            subChartMgr.TempSetTopObj(topObj);
            if (this.MatchRule(dbObj, theDoc, ruleAction)) matchList.push(node);
          } else if (node.fLayout.GetContainerType() !== enumContainerType.nonmrb) {
            if (this.MatchRule(dbObj, theDoc, ruleAction)) matchList.push(node);
          }
        }
      }
    }
    // Reset top box
    subChartMgr.ResetTopObj();

    return matchList;
  }

  DoClone(cloneFrom) {
    this.fActionSet = {};
    for (var key in cloneFrom.fActionSet) {
      var theSet = cloneFrom.fActionSet[key];
      var string = JSON.stringify(theSet);
      //Seriously Javascript I do not know why you are trying to make this difficult
      if (string != "{}") this.fActionSet[key] = JSON.parse(string);
    }
    //this.fActionSet = JSON.parse(JSON.stringify(cloneFrom.fActionSet));
  }

  DoRulesMatchBoxProp(compareTo, actionType) {
    var propList = [
      "fShapeEnabled",
      "fFillEnabled",
      "fBorderEnabled",
      "fBorderColorEnabled",
      "fLineColorEnabled",
      "fTriFlagEnabled",
      "fBSFlag"
    ];

    //Behold the glory of my Javascript XOR.
    if (
      this.fActionSet[actionType]
        ? !compareTo.fActionSet[actionType]
        : compareTo.fActionSet[actionType]
    )
      return false;

    for (var aPropertyIdx in propList) {
      var theProp = propList[aPropertyIdx];
      if (this.fActionSet[actionType][theProp] !== compareTo.fActionSet[actionType][theProp])
        return false;
    }

    if (this.GetShapeEnabled(actionType)) {
      if (this.GetBoxShapeType(actionType) != compareTo.GetBoxShapeType(actionType)) return false;
    }

    if (this.GetFillEnabled(actionType)) {
      if (this.GetBoxFillColor(actionType) != compareTo.GetBoxFillColor(actionType)) return false;
      if (this.GetBoxFillMode(actionType) != compareTo.GetBoxFillMode(actionType)) return false;
      if (
        this.GetBoxFillMode(actionType) != enumFillMode.solidFill &&
        this.GetBoxFillColorGradient(actionType) != compareTo.GetBoxFillColorGradient(actionType)
      )
        return false;
    }

    if (this.GetBorderEnabled(actionType)) {
      if (this.GetBorderThickness(actionType) != compareTo.GetBorderThickness(actionType))
        return false;

      if (this.GetBorderStyle(actionType) != compareTo.GetBorderStyle(actionType)) return false;

      if (this.GetBorderDashStyle(actionType) != compareTo.GetBorderDashStyle(actionType))
        return false;
    }

    if (this.GetBorderColorEnabled(actionType)) {
      if (this.GetBoxBorderColor(actionType) != compareTo.GetBoxBorderColor(actionType))
        return false;
    }

    if (this.GetLineColorEnabled(actionType)) {
      if (this.GetLineColor(actionType) != compareTo.GetLineColor(actionType)) return false;
    }

    if (this.GetLineDashEnabled(actionType)) {
      if (
        this.GetLineType(actionType) != compareTo.GetLineType(actionType) ||
        this.GetLineWeight(actionType) != compareTo.GetLineWeight(actionType)
      )
        return false;
    }

    if (this.GetTriFlagEnabled(actionType)) {
      if (this.GetTriColor1(actionType) != compareTo.GetTriColor1(actionType)) return false;
      if (this.GetTriColor2(actionType) != compareTo.GetTriColor2(actionType)) return false;
      if (this.GetTriColor3(actionType) != compareTo.GetTriColor3(actionType)) return false;
      if (this.GetTriPosition(actionType) != compareTo.GetTriPosition(actionType)) return false;
      if (this.GetTriLabel(actionType) != compareTo.GetTriLabel(actionType)) return false;
    }

    return true;
  }

  DoRulesMatchBSProp(compareTo) {
    if (
      this.fActionSet[kBranchStyleOverlayRule]
        ? !compareTo.fActionSet[kBranchStyleOverlayRule]
        : compareTo.fActionSet[kBranchStyleOverlayRule]
    )
      return false;

    if (this.GetBranchStyleFlag() != compareTo.GetBranchStyleFlag()) return false;

    return true;
  }

  DoRulesMatchFieldProp(compareTo, actionType) {
    if (
      this.fActionSet[actionType]
        ? !compareTo.fActionSet[actionType]
        : compareTo.fActionSet[actionType]
    )
      return false;

    if (this.GetTextColorEnabled(actionType) != compareTo.GetTextColorEnabled(actionType))
      return false;

    if (this.GetTextColorEnabled(actionType)) {
      if (this.GetTextColor(actionType) != compareTo.GetTextColor(actionType)) return false;
    }

    if (this.GetCellColorEnabled(actionType) != compareTo.GetCellColorEnabled(actionType))
      return false;

    if (this.GetCellColorEnabled(actionType)) {
      if (this.GetCellColor(actionType) != compareTo.GetCellColor(actionType)) return false;
    }

    if (this.GetTextStyleEnabled(actionType) != compareTo.GetTextStyleEnabled(actionType))
      return false;

    if (this.GetTextStyleEnabled(actionType)) {
      if (this.GetBoldStyle(actionType) != compareTo.GetBoldStyle(actionType)) return false;
      if (this.GetItalicStyle(actionType) != compareTo.GetItalicStyle(actionType)) return false;
      if (this.GetUnderlineStyle(actionType) != compareTo.GetUnderlineStyle(actionType))
        return false;
    }

    if (this.GetFieldName(actionType) != compareTo.GetFieldName(actionType)) return false;

    return true;
  }

  DoRulesMatchGrouping(compareTo) {
    if (
      this.fActionSet[kGroupingRule]
        ? !compareTo.fActionSet[kGroupingRule]
        : compareTo.fActionSet[kGroupingRule]
    )
      return false;

    var propList = [
      "fGroupingField",
      "fGroupingFieldEnabled",
      "fGroupingBoxStyle",
      "fChildrenBoxStyle",
      "fChildrenBoxStyleEnabled",
      "fGroupFromParking",
      "fGroupingThreshold",
      "fApplyToManagers",
      "fClearGroups",
      "fManagerGroupingStyle",
      "fManagerGroupingBoxStyle",
      "fManagerChildrenBoxStyle",
      "fManagerChildrenBoxStyleEnabled"
    ];

    // Canonical defaults for fields that may be absent in legacy rules so that
    // undefined and the explicit default value compare as equal.
    var groupingDefaults = {
      fApplyToManagers: false,
      fManagerGroupingStyle: "comgr",
      fManagerGroupingBoxStyle: null,
      fManagerChildrenBoxStyle: null,
      fManagerChildrenBoxStyleEnabled: false,
      fClearGroups: null
    };
    for (var aPropertyIdx in propList) {
      var theProp = propList[aPropertyIdx];
      var v1 = this.fActionSet[kGroupingRule][theProp];
      var v2 = compareTo.fActionSet[kGroupingRule][theProp];
      if (theProp in groupingDefaults) {
        if (v1 === undefined) v1 = groupingDefaults[theProp];
        if (v2 === undefined) v2 = groupingDefaults[theProp];
      }
      if (v1 !== v2) return false;
    }

    return true;
  }

  DoRulesMatch(compareTo) {
    var actionSet = this.fActionSet;

    if (
      (actionSet[kBoxStyleRule] || compareTo.fActionSet[kBoxStyleRule]) &&
      this.GetBoxStyleID(kBoxStyleRule) != compareTo.GetBoxStyleID(kBoxStyleRule)
    )
      return false;
    if (
      (actionSet[kPositionBoxStyleRule] || compareTo.fActionSet[kPositionBoxStyleRule]) &&
      this.GetBoxStyleID(kPositionBoxStyleRule) != compareTo.GetBoxStyleID(kPositionBoxStyleRule)
    )
      return false;
    if (
      (actionSet[kBoxOverlayRule] || compareTo.fActionSet[kBoxOverlayRule]) &&
      !this.DoRulesMatchBoxProp(compareTo, kBoxOverlayRule)
    )
      return false;
    if (
      (actionSet[kPositionBoxOverlayRule] || compareTo.fActionSet[kPositionBoxOverlayRule]) &&
      !this.DoRulesMatchBoxProp(compareTo, kPositionBoxOverlayRule)
    )
      return false;
    if (
      (actionSet[kFieldOverlayRule] || compareTo.fActionSet[kFieldOverlayRule]) &&
      !this.DoRulesMatchFieldProp(compareTo, kFieldOverlayRule)
    )
      return false;
    if (
      (actionSet[kPositionFieldOverlayRule] || compareTo.fActionSet[kPositionFieldOverlayRule]) &&
      !this.DoRulesMatchFieldProp(compareTo, kPositionFieldOverlayRule)
    )
      return false;
    if (
      (actionSet[kBranchStyleOverlayRule] || compareTo.fActionSet[kBranchStyleOverlayRule]) &&
      !this.DoRulesMatchBSProp(compareTo)
    )
      return false;
    if (
      (actionSet[kInheritedRule] || compareTo.fActionSet[kInheritedRule]) &&
      this.GetInheritTemplate() !== compareTo.GetInheritTemplate()
    )
      return false;
    if (
      (actionSet[kHighlightingRule] || compareTo.fActionSet[kHighlightingRule]) &&
      !this.DoRulesMatchHighlightProp(compareTo)
    )
      return false;
    if (
      (actionSet[kMasterPageRule] || compareTo.fActionSet[kMasterPageRule]) &&
      this.GetMasterName() !== compareTo.GetMasterName()
    )
      return false;
    if (
      (actionSet[kGroupingRule] || compareTo.fActionSet[kGroupingRule]) &&
      !this.DoRulesMatchGrouping(compareTo)
    )
      return false;

    return true;
  }

  UpdateFrom(updateFrom) {
    this.fRuleCriteriaType = updateFrom.fRuleCriteriaType;
    this.fRulePairList = [];
    this.fComplexDef = null;
    if (this.fRuleCriteriaType === "Basic Rule") {
      this.fLogicalOperator = updateFrom.fLogicalOperator;
      var n1 = updateFrom.GetNumRulePairs();
      for (var i = 0; i < n1; i++) {
        var rp = updateFrom.GetIndexRulePair(i);
        this.AddRulePair(
          rp.GetFieldName(),
          rp.GetFieldID(),
          rp.GetFieldClassName(),
          rp.GetComparison(),
          rp.GetValue(),
          rp.fCondition
        );
      }
    } else if (this.fRuleCriteriaType === "Complex Rule") this.fComplexDef = updateFrom.fComplexDef;

    this.DoUpdateFrom(updateFrom);
  }

  DoUpdateFrom(updateFrom) {
    if (updateFrom instanceof QChartTemplateRule) this.DoClone(updateFrom);
  }

  /**
   * @param {string} actionType
   * @returns {boolean}
   */
  HasActiveAction(actionType) {
    if (actionType === kDynHighlightRule) {
      return this.fActionSet[actionType];
    }

    return this.fActionSet[actionType] && this.fActionSet[actionType].fActive !== false;
  }

  SetBoxShapeType(boxShape, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBoxShapeType = boxShape;
  }

  GetBoxShapeType(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBoxShapeType : null;
  }

  SetBoxFillMode(fillMode, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBoxFillMode = fillMode;
  }

  GetBoxFillMode(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBoxFillMode : null;
  }

  SetBoxFillColor(fillColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBoxFillColor = fillColor;
  }

  GetBoxFillColor(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBoxFillColor : null;
  }

  SetBoxFillColorGradient(fillColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBoxFillColorGradient = fillColor;
  }

  GetBoxFillColorGradient(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBoxFillColorGradient : null;
  }

  SetLineColor(fillColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fLineColor = fillColor;
  }

  GetLineColor(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fLineColor : null;
  }

  SetLineColorEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fLineColorEnabled = enabled;
  }

  GetLineColorEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fLineColorEnabled : null;
  }

  SetLineDashEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fLineDashEnabled = enabled;
  }

  GetLineDashEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fLineDashEnabled : null;
  }

  SetLineType(lineType, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fLineType = lineType;
  }

  GetLineType(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fLineType : null;
  }

  SetLineWeight(lineWeight, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fLineWeight = lineWeight;
  }

  GetLineWeight(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fLineWeight : null;
  }

  SetTriFlagEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fTriFlagEnabled = enabled;
  }

  GetTriFlagEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTriFlagEnabled : null;
  }

  SetBranchStyleFlag(bsOption) {
    if (!this.fActionSet[kBranchStyleOverlayRule]) this.fActionSet[kBranchStyleOverlayRule] = {};
    this.fActionSet[kBranchStyleOverlayRule].fBSFlag = bsOption;
  }

  GetBranchStyleFlag() {
    return this.fActionSet[kBranchStyleOverlayRule]
      ? this.fActionSet[kBranchStyleOverlayRule].fBSFlag
      : null;
  }

  SetInheritTemplate(templateName) {
    if (!this.fActionSet[kInheritedRule]) this.fActionSet[kInheritedRule] = {};
    this.fActionSet[kInheritedRule].fInheritTemplate = templateName;
  }

  GetInheritTemplate() {
    return this.fActionSet[kInheritedRule]
      ? this.fActionSet[kInheritedRule].fInheritTemplate
      : null;
  }

  SetRuleHighlightColor(highlightColor) {
    if (!this.fActionSet[kHighlightingRule]) this.fActionSet[kHighlightingRule] = {};
    this.fActionSet[kHighlightingRule].fHighlightColor = highlightColor;
  }

  SetRuleHighlightActive(isActive) {
    this.fActionSet[kHighlightingRule].fActive = isActive;
  }

  DoRulesMatchHighlightProp(compareTo) {
    if (
      this.fActionSet[kHighlightingRule]
        ? !compareTo.fActionSet[kHighlightingRule]
        : compareTo.fActionSet[kHighlightingRule]
    )
      return false;
    if (this.GetRuleHighlightColor() !== compareTo.GetRuleHighlightColor()) return false;
    return true;
  }

  GetRuleHighlightColor() {
    return this.fActionSet[kHighlightingRule] && this.fActionSet[kHighlightingRule].fHighlightColor
      ? this.fActionSet[kHighlightingRule].fHighlightColor
      : "#FFFFFF";
  }

  SetGroupingFieldEnabled(doEnable) {
    this.fActionSet[kGroupingRule].fGroupingFieldEnabled = doEnable;
  }

  GetGroupingFieldEnabled() {
    return this.fActionSet[kGroupingRule]
      ? this.fActionSet[kGroupingRule].fGroupingFieldEnabled
      : null;
  }

  SetGroupingBoxStyle(theStyle) {
    if (!this.fActionSet[kGroupingRule]) this.fActionSet[kGroupingRule] = {};
    this.fActionSet[kGroupingRule].fGroupingBoxStyle = theStyle;
  }

  GetGroupingBoxStyle() {
    return this.fActionSet[kGroupingRule] ? this.fActionSet[kGroupingRule].fGroupingBoxStyle : null;
  }

  SetGroupingField(theField) {
    this.fActionSet[kGroupingRule].fGroupingField = theField;
  }

  GetGroupingField() {
    return this.fActionSet[kGroupingRule] ? this.fActionSet[kGroupingRule].fGroupingField : null;
  }

  SetChildrenBoxStyleEnabled(enabled) {
    this.fActionSet[kGroupingRule].fChildrenBoxStyleEnabled = enabled;
  }

  GetChildrenBoxStyleEnabled() {
    return this.fActionSet[kGroupingRule]
      ? this.fActionSet[kGroupingRule].fChildrenBoxStyleEnabled
      : null;
  }

  SetChildrenBoxStyle(theStyle) {
    this.fActionSet[kGroupingRule].fChildrenBoxStyle = theStyle;
  }

  GetChildrenBoxStyle() {
    return this.fActionSet[kGroupingRule] ? this.fActionSet[kGroupingRule].fChildrenBoxStyle : null;
  }

  SetGroupingThreshold(threshold) {
    if (!this.fActionSet[kGroupingRule]) this.fActionSet[kGroupingRule] = {};
    this.fActionSet[kGroupingRule].fGroupingThreshold = threshold;
  }

  GetGroupingThreshold() {
    return this.fActionSet[kGroupingRule] ? this.fActionSet[kGroupingRule].fGroupingThreshold : 2;
  }

  SetApplyToManagers(doMgrs) {
    this.fActionSet[kGroupingRule].fApplyToManagers = doMgrs;
  }

  GetApplyToManagers(isGroupingRule) {
    let applyToManagers = true;
    if (this.fActionSet[kGroupingRule] && this.fActionSet[kGroupingRule].fApplyToManagers == true)
      return true;

    return !isGroupingRule;
  }

  SetManagerGroupingStyle(style) {
    this.fActionSet[kGroupingRule].fManagerGroupingStyle = style;
  }

  GetManagerGroupingStyle() {
    return this.fActionSet[kGroupingRule]?.fManagerGroupingStyle ?? "comgr";
  }

  SetManagerGroupingBoxStyle(style) {
    this.fActionSet[kGroupingRule].fManagerGroupingBoxStyle = style;
  }

  GetManagerGroupingBoxStyle() {
    return this.fActionSet[kGroupingRule]?.fManagerGroupingBoxStyle ?? null;
  }

  SetManagerChildrenBoxStyle(style) {
    this.fActionSet[kGroupingRule].fManagerChildrenBoxStyle = style;
  }

  GetManagerChildrenBoxStyle() {
    return this.fActionSet[kGroupingRule]?.fManagerChildrenBoxStyle ?? null;
  }

  SetManagerChildrenBoxStyleEnabled(enabled) {
    this.fActionSet[kGroupingRule].fManagerChildrenBoxStyleEnabled = enabled;
  }

  GetManagerChildrenBoxStyleEnabled() {
    return this.fActionSet[kGroupingRule]?.fManagerChildrenBoxStyleEnabled ?? false;
  }

  SetClearGroups(doClear) {
    this.fActionSet[kGroupingRule].fClearGroups = doClear;
  }

  GetClearGroups() {
    return this.fActionSet[kGroupingRule] ? this.fActionSet[kGroupingRule].fClearGroups : null;
  }

  SetGroupFromParking(doFromParking) {
    this.fActionSet[kGroupingRule].fGroupFromParking = doFromParking;
  }

  GetGroupFromParking() {
    return this.fActionSet[kGroupingRule].fGroupFromParking;
  }

  SetTriColor1(triColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fTriColor1 = triColor;
  }

  GetTriColor1(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTriColor1 : null;
  }

  SetTriColor2(triColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fTriColor2 = triColor;
  }

  GetTriColor2(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTriColor2 : null;
  }

  SetTriColor3(triColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fTriColor3 = triColor;
  }

  GetTriColor3(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTriColor3 : null;
  }

  SetTriPosition(triPosition, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fTriPosition = triPosition;
  }

  GetTriPosition(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTriPosition : null;
  }

  SetTriLabel(triLabel, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fTriLabel = triLabel;
  }

  GetTriLabel(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTriLabel : null;
  }

  // Border Thickness
  SetBorderThickness(thickness, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBorderThickness = thickness;
  }

  GetBorderThickness(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBorderThickness : null;
  }

  // Border Dash Style
  SetBorderDashStyle(dashStyle, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBorderDashStyle = dashStyle;
  }

  GetBorderDashStyle(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBorderDashStyle : null;
  }

  // Border Style
  SetBorderStyle(borderStyle, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBorderStyle = borderStyle;
  }

  GetBorderStyle(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBorderStyle : null;
  }

  // Box Border Color
  SetBoxBorderColor(borderColor, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBoxBorderColor = borderColor;
  }

  GetBoxBorderColor(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBoxBorderColor : null;
  }

  // Box Shape
  SetShapeEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fShapeEnabled = enabled;
  }

  GetShapeEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fShapeEnabled : null;
  }

  SetTextColorEnabled(enabled, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fTextColorEnabled = enabled;
  }

  GetTextColorEnabled(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTextColorEnabled : null;
  }

  SetTextColor(textColor, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fTextColor = textColor;
  }

  GetTextColor(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTextColor : null;
  }

  SetCellColorEnabled(enabled, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fCellColorEnabled = enabled;
  }

  GetCellColorEnabled(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fCellColorEnabled : null;
  }

  SetCellColor(cellColor, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fCellColor = cellColor;
  }

  GetCellColor(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fCellColor : null;
  }

  SetTextStyleEnabled(enabled, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fTextStyleEnabled = enabled;
  }

  GetTextStyleEnabled(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fTextStyleEnabled : null;
  }

  SetBoldStyle(boldStyle, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fCellBold = boldStyle;
  }

  GetBoldStyle(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fCellBold : null;
  }

  SetItalicStyle(italicStyle, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fCellItalic = italicStyle;
  }

  GetItalicStyle(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fCellItalic : null;
  }

  SetUnderlineStyle(underlineStyle, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fCellUnderline = underlineStyle;
  }

  GetUnderlineStyle(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fCellUnderline : null;
  }

  SetFieldName(fieldName, actionType = kFieldOverlayRule) {
    if (!this.fActionSet[actionType]) this.fActionSet[actionType] = {};
    this.fActionSet[actionType].fFieldName = fieldName;
  }

  GetFieldName(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fFieldName : null; //TODO: Should support multiple fields
  }

  /**
   *
   * @param {string} actionType
   * @param {QXDoc} docRef
   * @returns {string}
   */
  GetFieldLabel(actionType, docRef) {
    const fieldName = this.GetFieldName(actionType);
    if (!docRef || !fieldName) {
      return fieldName ?? "";
    }

    const className = {
      [kFieldOverlayRule]: "Box",
      [kPositionFieldOverlayRule]: "Position"
    }[actionType];

    const objDb = docRef.GetObjDB();
    const objClass = objDb?.GetClassByName(className);
    const fieldDef = objClass?.LookupFieldDef(fieldName);
    if (!fieldDef) {
      return fieldName;
    }

    return GetFieldLabel(fieldDef, true);
  }

  SetFillEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fFillEnabled = enabled;
  }

  GetFillEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fFillEnabled : null;
  }

  SetBorderEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBorderEnabled = enabled;
  }

  GetBorderEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBorderEnabled : null;
  }

  SetBorderColorEnabled(enabled, actionType = kBoxOverlayRule) {
    this.fActionSet[actionType].fBorderColorEnabled = enabled;
  }

  GetBorderColorEnabled(actionType = kBoxOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fBorderColorEnabled : null;
  }

  GetCellShown(actionType = kFieldOverlayRule) {
    return this.fActionSet[actionType] ? this.fActionSet[actionType].fCellShown : null;
  }

  SetCellShown(toggle, actionType = kFieldOverlayRule) {
    this.fActionSet[actionType].fCellShown = toggle;
  }

  ApplyFieldsProps(aLayout, actionType) {
    const actionHierarchy = this.hierarchyMap[actionType];
    const layoutHierarchy = aLayout.GetHierarchyType();
    if (actionHierarchy !== layoutHierarchy) {
      return;
    }

    var fieldName = this.GetFieldName(actionType);
    var nCells = aLayout.GetNumCells();
    for (var i = 0; i < nCells; i++) {
      var aCellDesc = aLayout.GetIndexCellDesc(i);
      //TODO: Making these rules more portable between data sets it would be super useful to also try on field ID.
      var cellFieldName = aCellDesc.GetFieldDisplayName(); //GetFieldSaveName();
      if (fieldName == cellFieldName) {
        if (this.GetTextStyleEnabled(actionType)) {
          var isBold = this.GetBoldStyle(actionType);
          var isItalic = this.GetItalicStyle(actionType);
          var isUnderline = this.GetUnderlineStyle(actionType);
          aCellDesc.SetTextItalic(isItalic);
          aCellDesc.SetTextBold(isBold);
          aCellDesc.SetTextUnderline(isUnderline);
        }

        if (this.GetTextColorEnabled(actionType)) {
          var tColor = this.GetTextColor(actionType);
          aCellDesc.SetFontColor(tColor);
        }

        if (this.GetCellColorEnabled(actionType)) {
          var cColor = this.GetCellColor(actionType);
          aCellDesc.SetCellFillMode(enumFillMode.solidFill);
          aCellDesc.SetCellFillColor(cColor);
        }

        if (this.GetCellShown(actionType) === false)
          aCellDesc.SetFieldDisplayMode(enumFieldDisplayMode.Hidden);
      }
    }
  }

  ApplyBoxOverlayProps(aLayout, actionType) {
    const actionHierarchy = this.hierarchyMap[actionType];
    const layoutHierarchy = aLayout.GetHierarchyType();
    if (actionHierarchy !== layoutHierarchy) {
      return;
    }

    if (this.GetShapeEnabled(actionType)) aLayout.SetBoxShapeType(this.GetBoxShapeType(actionType));

    if (this.GetFillEnabled(actionType)) {
      aLayout.SetBoxFillColor(this.GetBoxFillColor(actionType));
      if (
        this.GetBoxFillMode(actionType) &&
        this.GetBoxFillMode(actionType) !== enumFillMode.solidFill
      ) {
        aLayout.SetBoxFillColorGradient(this.GetBoxFillColorGradient(actionType));
        aLayout.SetBoxFillMode(this.GetBoxFillMode(actionType));
      } else aLayout.SetBoxFillMode(enumFillMode.solidFill);
    }

    if (this.GetBorderEnabled(actionType)) {
      if (this.GetBorderThickness(actionType) != null)
        aLayout.SetBorderThickness(this.GetBorderThickness(actionType));
      if (this.GetBorderStyle(actionType) != null)
        aLayout.SetBorderStyle(this.GetBorderStyle(actionType));
      if (this.GetBorderDashStyle(actionType) != null)
        aLayout.SetDashStyle(this.GetBorderDashStyle(actionType));
    }

    if (this.GetBorderColorEnabled(actionType))
      aLayout.SetBoxBorderColor(this.GetBoxBorderColor(actionType));

    if (this.GetLineColorEnabled(actionType))
      aLayout.SetParentConnectorColor(this.GetLineColor(actionType));

    if (this.GetLineDashEnabled(actionType)) {
      var dashStyle = this.GetLineType(actionType); // enumDashStyle.stddash;
      aLayout.SetParentConnectorDashStyle(dashStyle);
      var weight = this.GetLineWeight(actionType);
      aLayout.SetParentConnectorWeightStyle(weight);
    }

    if (this.GetTriFlagEnabled(actionType)) {
      aLayout.SetFlagType(enumFlagType.triupperright);
      if (this.GetTriColor1(actionType) != null)
        aLayout.SetFlagColor1(this.GetTriColor1(actionType));
      if (this.GetTriColor2(actionType) != null)
        aLayout.SetFlagColor2(this.GetTriColor2(actionType));
      if (this.GetTriColor3(actionType) != null)
        aLayout.SetFlagColor3(this.GetTriColor3(actionType));
      if (this.GetTriPosition(actionType) != null)
        aLayout.SetTriPosition(this.GetTriPosition(actionType));

      if (this.GetTriLabel(actionType) != null) aLayout.SetFlagLabel(this.GetTriLabel(actionType));
    }
  }

  ApplyBoxProps(aLayout) {
    //TODO: This is written in a pretty weird way...why doesn't a layout have a method to apply properties?
    var actionSet = this.fActionSet;
    if (actionSet[kFieldOverlayRule]) {
      this.ApplyFieldsProps(aLayout, kFieldOverlayRule);
    }

    if (actionSet[kPositionFieldOverlayRule]) {
      this.ApplyFieldsProps(aLayout, kPositionFieldOverlayRule);
    }

    if (actionSet[kBranchStyleOverlayRule]) {
      var bsStyle = null;
      switch (this.GetBranchStyleFlag()) {
        case enumBSFlagOptions.assistleft:
          bsStyle = enumBranchStyle.assistantleft;
          break;
        case enumBSFlagOptions.assistright:
          bsStyle = enumBranchStyle.assistantright;
          break;
      }
      if (bsStyle != null) aLayout.SetBranchStyle(bsStyle);
    }

    if (actionSet[kBoxOverlayRule]) {
      this.ApplyBoxOverlayProps(aLayout, kBoxOverlayRule);
    }

    if (actionSet[kPositionBoxOverlayRule]) {
      this.ApplyBoxOverlayProps(aLayout, kPositionBoxOverlayRule);
    }
  }

  Clone() {
    var clone = new QChartTemplateRule();
    clone.fRuleCriteriaType = this.fRuleCriteriaType;
    clone.fRulePairList = [];
    var n = this.GetNumRulePairs();
    for (var i = 0; i < n; i++) {
      var rp = this.GetIndexRulePair(i);
      var clonePair = rp.Clone();
      clone.fRulePairList.push(clonePair);
    }
    clone.fActionSet = JSON.parse(JSON.stringify(this.fActionSet));
    if (this.fComplexDef) clone.fComplexDef = this.fComplexDef;
    clone.fLogicalOperator = this.fLogicalOperator;

    return clone;
  }

  /**
   *
   * @param {QBoxStylesManager} boxStylesMgr
   * @param {QChartTemplatesMgr} chartTemplateMgr
   * @param {QXDoc} docRef
   * @returns {string[]}
   */
  GenerateActionStr(boxStylesMgr, chartTemplateMgr, docRef) {
    var actions = [];

    for (var action in this.fActionSet) {
      var actionType = action;
      var styleName = "";
      switch (actionType) {
        case kBoxOverlayRule:
        case kPositionBoxOverlayRule:
          if (this.GetBoxShapeType(actionType) != null) styleName += ", " + appstr("Box Shape");
          if (this.GetFillEnabled(actionType)) {
            if (this.GetBoxFillMode(actionType) == enumFillMode.noFill)
              styleName += ", " + appstr("nofill");
            else if (this.GetBoxFillMode(actionType) == enumFillMode.linearGradientDown)
              styleName += ", " + appstr("Box Color") + ", " + appstr("gradientfill");
            else styleName += ", " + appstr("Box Color");
          }
          if (this.GetBorderColorEnabled(actionType) && this.GetBoxBorderColor(actionType))
            styleName += ", " + appstr("Border Color");
          if (this.GetLineColorEnabled(actionType) && this.GetLineColor(actionType))
            styleName += ", " + appstr("Connecting Line Color");
          if (
            this.GetLineDashEnabled(actionType) &&
            (this.GetLineType(actionType) || this.GetLineWeight(actionType))
          )
            styleName += ", " + appstr("Connecting Line Style");
          if (this.GetBorderEnabled(actionType)) {
            if (this.GetBorderThickness(actionType) != null)
              styleName +=
                ", " +
                this.GetBorderThickness(actionType).toString() +
                "px " +
                appstr("Border Style");

            switch (this.GetBorderStyle(actionType)) {
              case 0:
                styleName += ", " + appstr("noborder");
                break;
              case 1:
                styleName += ", " + appstr("singleborder");
                break;
              case 2:
                styleName += ", " + appstr("dblborder");
                break;
            }
            if (this.GetBorderDashStyle(actionType) != null) {
              switch (this.GetBorderDashStyle(actionType)) {
                case 0:
                  styleName += ", " + appstr("solidborder");
                  break;
                case 1:
                  styleName += ", " + appstr("dashedborder");
                  break;
              }
            }
          }
          if (this.GetTriFlagEnabled(actionType)) {
            //Triangle label
            if (this.GetTriPosition(actionType) !== 1) styleName += ", " + appstr("triangle");
            else styleName += ", " + appstr("lefttriangle");

            if (this.GetTriColor3(actionType)) {
              styleName += " (" + appstr("twocolortri") + ")";
            }

            if (this.GetTriLabel(actionType)) {
              styleName += " '" + this.GetTriLabel(actionType) + "'";
            }
          }

          if (styleName) {
            let actionName = appstr("Box Properties");
            if (actionType === kPositionBoxOverlayRule) {
              actionName = `${appstr("Box Properties")} (${appstr("Position box")})`;
            }

            styleName = `${actionName}: ${styleName.slice(styleName.indexOf(",") + 1, styleName.length)}`;
          }
          break;
        case kFieldOverlayRule:
        case kPositionFieldOverlayRule:
          var strBuilder = "";
          const fieldLabel = this.GetFieldLabel(actionType, docRef);

          if (this.GetCellShown(actionType) === false) strBuilder += ", " + appstr("Hide Field");
          else {
            if (this.GetCellColorEnabled(actionType)) strBuilder += ", " + appstr("solidfill");
            if (this.GetTextColorEnabled(actionType)) strBuilder += ", " + appstr("color");
            if (
              this.GetBoldStyle(actionType) ||
              this.GetItalicStyle(actionType) ||
              this.GetUnderlineStyle(actionType)
            )
              strBuilder += ", " + appstr("Font Face");
          }
          if (strBuilder && fieldLabel) {
            styleName =
              "<" +
              fieldLabel +
              ">: " +
              strBuilder.slice(strBuilder.indexOf(",") + 1, strBuilder.length);
          }
          break;
        case kBranchStyleOverlayRule:
          styleName = appstr("Branch Style") + ": ";
          var branchStyleName = GetBSOption(this.GetBranchStyleFlag());
          styleName += branchStyleName;
          break;
        case kMasterPageRule:
          styleName = appstr("Master Page") + ": " + this.GetMasterName();
          break;
        case kInheritedRule:
          var theTemplateId = this.GetInheritTemplate();
          var { template } = chartTemplateMgr.FindTemplateByIDAllSets(theTemplateId, true);
          styleName =
            Sappstr("Template") + ": " + template?.GetTemplateName() ?? appstr("viewnotfound");
          break;
        case kHighlightingRule:
          styleName = appstr("Highlight") + ": " + this.GetRuleHighlightColor();
          break;
        case kGroupingRule:
          styleName = "[" + appstr("Grouping") + "]";
          if (this.GetGroupingFieldEnabled() && this.GetGroupingField()) {
            styleName += " " + appstr("combinerecords") + ": " + this.GetGroupingField() + ", ";
          }
          if (this.GetChildrenBoxStyleEnabled() && this.GetChildrenBoxStyle()) {
            styleName +=
              " " +
              appstr("Apply Box Style To Members") +
              ": " +
              boxStylesMgr.GetBoxStyleNameFromID(this.GetChildrenBoxStyle(), false) +
              ", ";
          }
          var styleID = this.GetGroupingBoxStyle();
          styleName +=
            appstr("Container") + ": " + boxStylesMgr.GetBoxStyleNameFromID(styleID, false);
          break;
        default:
          //Just ye olde boxstyle
          var boxStyleID = this.GetBoxStyleID(actionType);
          styleName = boxStylesMgr.GetBoxStyleNameFromID(boxStyleID, false);
          var styleSet = boxStylesMgr.GetBoxStyleSetFromID(boxStyleID);
          if (actionType === kPositionBoxStyleRule) {
            styleSet = `${styleSet} (${appstr("Position box")})`;
          }
          styleName = styleSet + ": " + styleName;
          break;
      }
      if (styleName) {
        actions.push(styleName);
      }
    }

    return actions;
  }
}
